import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import {
  CompanyShareableType,
  getV1CompaniesCompanyIdCompanyShares,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import AutocompleteBoolean from '~components/FormFields/CommonAutocompletes/AutocompleteBoolean';
import AutocompleteCompanyTypes from '~components/FormFields/CommonAutocompletes/AutocompleteCompanyTypes';
import AutocompleteDriversTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteDriversTypeahead';
import AutocompleteEquipmentsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteEquipmentsTypeahead';
import Label from '~components/FormFields/v2/Label';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import { CompanyShare, CompanyShareableBasic } from '~hooks/useCompanyShares/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  AccountConfigurationDto,
  accountConfigurationSchema,
  setAccountConfigurationSchemaDefaultValues,
} from './accountConfigurationSchema';

export type AccountConfigurationPanelHandler = {
  onSubmit: () => Promise<AccountConfigurationDto | undefined>;
};

interface AccountConfigurationPanelProviderProps {
  account?: Account;
  onIsConnectedAccountChange: (isConnectedAccount: boolean) => void;
}

const AccountConfigurationPanelProvider = forwardRef<
  AccountConfigurationPanelHandler,
  AccountConfigurationPanelProviderProps
>(function AccountConfigurationPanelProvider(props, ref) {
  const companyId = props.account?.company?.id;
  const connectedCompanyId = props.account?.connectedCompany?.id;

  const shouldLoadCompanyShares = connectedCompanyId && companyId;

  const [companyShares, setCompanyShares] = useState<{
    drivers: CompanyShareableBasic[];
    equipment: CompanyShareableBasic[];
    isLoading: boolean;
  }>({ isLoading: Boolean(shouldLoadCompanyShares), drivers: [], equipment: [] });

  const fetchCompanyShares = useCallback(
    async (
      companyId: string,
      connectedCompanyId: string,
      companyShareableType: CompanyShareableType,
    ) => {
      const response = await getV1CompaniesCompanyIdCompanyShares({
        path: { 'company-id': companyId },
        query: {
          'filter[company_shareable_type]': companyShareableType,
          'filter[between_company_id]': connectedCompanyId,
        },
      });
      const parsed = response.data.data.map((item) => CompanyShare.parse(item));

      return parsed
        .filter((item) => item.senderCompany.id === companyId)
        .map((share) => share.companyShareable);
    },
    [],
  );

  useEffect(() => {
    if (shouldLoadCompanyShares) {
      Promise.all([
        fetchCompanyShares(companyId, connectedCompanyId, CompanyShareableType.USER),
        fetchCompanyShares(companyId, connectedCompanyId, CompanyShareableType.EQUIPMENT),
      ])
        .then(([drivers, equipment]) => {
          setCompanyShares({ isLoading: false, drivers, equipment });
        })
        .catch((error) => {
          setCompanyShares({ isLoading: false, drivers: [], equipment: [] });

          connection.handleRequestError(
            error,
            t('error_messages.company_shares.failed_to_fetch_company_shares'),
          );
        });
    }
  }, [shouldLoadCompanyShares, companyId, connectedCompanyId, fetchCompanyShares]);

  if (companyShares.isLoading) {
    return <LoadingSpinner isVisible />;
  }

  return (
    <AccountConfigurationPanel
      {...props}
      ref={ref}
      sharedDrivers={companyShares.drivers}
      sharedEquipment={companyShares.equipment}
    />
  );
});

interface AccountConfigurationPanelProps extends AccountConfigurationPanelProviderProps {
  sharedDrivers: CompanyShareableBasic[];
  sharedEquipment: CompanyShareableBasic[];
}

const AccountConfigurationPanel = forwardRef<
  AccountConfigurationPanelHandler,
  AccountConfigurationPanelProps
>(function AccountConfigurationPanel(
  { account, onIsConnectedAccountChange, sharedDrivers, sharedEquipment },
  ref,
) {
  const { userStore } = useStores();

  const isEditing = Boolean(account?.id);
  const companyId = getEffectiveUserCompanyId(userStore);

  const { control, handleSubmit, setValue, watch } = useForm<AccountConfigurationDto>({
    resolver: yupResolver(accountConfigurationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setAccountConfigurationSchemaDefaultValues(
      account,
      sharedDrivers,
      sharedEquipment,
    ),
  });

  const isConnectedAccount = watch('isConnectedAccount');
  const sharedDriversList = watch('drivers');
  const sharedEquipmentList = watch('equipment');

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    onIsConnectedAccountChange(isConnectedAccount);
  }, [isConnectedAccount, onIsConnectedAccountChange]);

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={isConnectedAccount}
            onChange={(_event, checked) => setValue('isConnectedAccount', checked)}
          />
        }
        disabled={isEditing}
        label={t('settings.customers.is_connected_account_switch')}
      />

      <Collapse in={isConnectedAccount}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridAutoColumns: '1fr 1fr',
            mt: 2,
            width: '100%',
          }}
        >
          <Box>
            <AutocompleteCompanyTypes
              control={control}
              isDisabled={isEditing}
              isRequired={isConnectedAccount}
              label={t('form_fields.company_type')}
              name="companyType"
            />

            <Typography variant="body2" sx={{ mt: '2px' }}>
              {t('account.form.select_owner_operator')}
            </Typography>
          </Box>
          <Box />

          <AutocompleteDriversTypeahead
            companyId={companyId}
            control={control}
            disableClearable
            isMultiple
            label={t('account.form.direct_dispatch_drivers')}
            limitTags={1}
            name="drivers"
          />

          <Box sx={{ gridColumn: '2 / 3' }}>
            <CompanySharesList
              label={t('account.form.direct_dispatch_drivers_list')}
              list={sharedDriversList ?? undefined}
            />
          </Box>

          <AutocompleteEquipmentsTypeahead
            companyId={companyId}
            control={control}
            disableClearable
            isMultiple
            label={t('account.form.share_equipment')}
            limitTags={1}
            name="equipment"
          />

          <Box sx={{ gridColumn: '2 / 3' }}>
            <CompanySharesList
              label={t('account.form.shared_equipment_list')}
              list={sharedEquipmentList ?? undefined}
            />
          </Box>

          <AutocompleteBoolean
            control={control}
            disableClearable
            label={t('account.form.auto_accept_jobs')}
            name="autoAcceptOrders"
          />

          <Box sx={{ gridColumn: '1 / 3' }}>
            <Controller
              control={control}
              name="showUserPhoneNumber"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value ?? true}
                      onChange={(event) => field.onChange(event.target.checked)}
                      size="small"
                    />
                  }
                  label={t('account.form.show_user_phone_number')}
                />
              )}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

interface CompanySharesListProps {
  label: string;
  list?: Array<{ label: string }>;
}

function CompanySharesList({ label, list }: CompanySharesListProps) {
  return (
    <>
      <Label>{label}</Label>

      <Typography variant="body2" sx={{ mt: '5px' }}>
        {list?.length
          ? list.map((option) => option.label).join(', ')
          : t('common.none_selected')}
      </Typography>
    </>
  );
}

export default AccountConfigurationPanelProvider;
